import Big from 'big.js';
import { FinancerPendingDetailDTO } from '../../dto/FinancerDistributionDTO';
import { EDistributionContractType } from '../../enums/EDistributionContractType';
import { EPayerType } from '../../enums/EPayerType';

/** Filas por página que se piden al llegar al final del scroll. */
export const PAGE_SIZE = 50;

/** Parámetros de la última búsqueda: resumen, detalle y creación usan estos, no los filtros sin aplicar. */
export interface AppliedSearch {
    billingEntityId: number;
    professionalId: number; // -1 = todos
    healthEntityId: number; // -1 = todas
    fromDate: Date;
    toDate: Date;
    formStates: number[];
    orderStates: number[];
    paymentMethods: number[];
    /** Quién paga la orden (EPayerType); vacío = todos. */
    payerTypes: number[];
}

export interface SelectOption {
    label: string;
    value: number;
}

export interface StateBadgeConfig {
    label: string;
    bg: string;
    color: string;
}

// ── Formato ──

export function formatDateTimeParam(date: Date, endOfDay = false): string {
    return `${formatDateParam(date)}T${endOfDay ? '23:59:59' : '00:00:00'}`;
}

export function formatDateParam(date: Date): string {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
}

/** Solo presentación: un Big se pasa a number recién acá, ya redondeado a 2 decimales. */
export function formatMoney(amount: Big | number | null | undefined): string {
    if (amount == null) return '—';
    const value = amount instanceof Big ? Number(amount.toFixed(2)) : amount;
    return `$ ${value.toLocaleString('es-AR', { maximumFractionDigits: 2, style: 'decimal' })}`;
}

export function formatDate(dateStr: string | null | undefined): string {
    if (!dateStr) return '—';
    const d = new Date(dateStr);
    return isNaN(d.getTime()) ? dateStr : d.toLocaleDateString('es-AR');
}

export function formatHonorariumPercentage(d: FinancerPendingDetailDTO): string {
    if (d.distributionContractType === EDistributionContractType.FIXED_AMOUNT) return '—';
    return d.honorariumPercentage != null ? `${d.honorariumPercentage}%` : '—';
}

/** Parámetros comunes a resumen y detalle, a partir de la búsqueda aplicada. */
export function buildPendingQueryParams(search: AppliedSearch, professionalId: number, offset: number, limit = PAGE_SIZE) {
    return {
        fromDatetime: formatDateTimeParam(search.fromDate),
        toDatetime: formatDateTimeParam(search.toDate, true),
        professionalId,
        billingEntityId: search.billingEntityId,
        healthEntityId: search.healthEntityId,
        formStateTypes: emptyToUndefined(search.formStates),
        benefitOrderStates: emptyToUndefined(search.orderStates),
        paymentMethodIds: emptyToUndefined(search.paymentMethods),
        payerTypes: emptyToUndefined(search.payerTypes),
        offset,
        limit,
    };
}

export function emptyToUndefined(values: number[]): number[] | undefined {
    return values.length > 0 ? values : undefined;
}

// ── Estados ──

export const BENEFIT_ORDER_STATES: Record<number, StateBadgeConfig> = {
    1: { label: 'Pend. facturacion', bg: '#F3F4F6', color: '#6B7280' },
    2: { label: 'Facturada', bg: '#DBEAFE', color: '#1E40AF' },
    3: { label: 'Cobrada', bg: '#D1FAE5', color: '#065F46' },
    4: { label: 'Pend. realizacion', bg: '#FEF3C7', color: '#92400E' },
    31: { label: 'Pago en garantia', bg: '#EDE9FE', color: '#5B21B6' },
    32: { label: 'Garantia devuelta', bg: '#D1FAE5', color: '#065F46' },
    33: { label: 'Garantia vencida', bg: '#FEE2E2', color: '#991B1B' },
    51: { label: 'Anulado', bg: '#FEE2E2', color: '#991B1B' },
    52: { label: 'Rechazado', bg: '#FEE2E2', color: '#991B1B' },
};

export const FORM_STATE_TYPES: Record<number, StateBadgeConfig> = {
    1: { label: 'Anulado', bg: '#FEE2E2', color: '#991B1B' },
    2: { label: 'Pendiente', bg: '#FEF3C7', color: '#92400E' },
    3: { label: 'Pagado', bg: '#D1FAE5', color: '#065F46' },
    4: { label: 'Registrado', bg: '#DBEAFE', color: '#1E40AF' },
};

export function unknownStateBadge(state: number): StateBadgeConfig {
    return { label: `Estado ${state}`, bg: '#F3F4F6', color: '#6B7280' };
}

export const BENEFIT_ORDER_STATE_OPTIONS: SelectOption[] = [
    { label: 'Pend. facturación', value: 1 },
    { label: 'Facturada', value: 2 },
    { label: 'Cobrada', value: 3 },
    { label: 'Pend. realización', value: 4 },
    { label: 'Pago en garantía', value: 31 },
    { label: 'Garantía devuelta', value: 32 },
    { label: 'Garantía vencida', value: 33 },
    { label: 'Anulado', value: 51 },
    { label: 'Rechazado', value: 52 },
];

/** Pagadores que usan honorarios (la tabla payertype también tiene "Institución", que no aplica acá). */
export const PAYER_TYPE_OPTIONS: SelectOption[] = [
    { label: 'Paciente', value: EPayerType.PATIENT },
    { label: 'Financiador', value: EPayerType.HEALTH_ENTITY },
];

export const FORM_STATE_OPTIONS: SelectOption[] = [
    { label: 'Anulado', value: 1 },
    { label: 'Pendiente', value: 2 },
    { label: 'Pagado', value: 3 },
    { label: 'Registrado', value: 4 },
];
